// Заглушка загрузки (components/Skeleton/README.md) в форме настоящего содержимого.
// Появляется, только если ожидание дольше 0,3 с (FR-040а): до этого — пусто, экран не прыгает.
import type { CSSProperties, ReactNode } from 'react';
import { cx } from '../lib/cx.ts';
import { LOADING_DELAY_MS, useDelayedFlag } from '../lib/use-delayed.ts';

export interface SkeletonProps {
  /** Идёт ли загрузка. */
  active?: boolean;
  /** Форма содержимого: полосы SkeletonBar. По умолчанию — три строки списка. */
  children?: ReactNode;
  delay?: number;
  /** Высота, которую заглушка держит до появления, чтобы раскладка не прыгала. */
  minHeight?: CSSProperties['minHeight'];
  className?: string;
}

export function Skeleton({
  active = true,
  children,
  delay = LOADING_DELAY_MS,
  minHeight,
  className,
}: SkeletonProps) {
  const shown = useDelayedFlag(active, delay);
  if (!active) return null;
  return (
    <div
      aria-busy="true"
      aria-label="Загрузка…"
      role="status"
      className={cx('kp-skeleton', className)}
      style={{ minHeight }}
    >
      {shown
        ? (children ?? (
            <>
              <SkeletonRow width="46%" />
              <SkeletonRow width="62%" />
              <SkeletonRow width="38%" />
            </>
          ))
        : null}
    </div>
  );
}

/** Строка списка: кружок, название и короткое значение справа (как в образце «Табеля»). */
export function SkeletonRow({ width = '50%' }: { width?: CSSProperties['width'] }) {
  return (
    <div className="t-row kp-sk-row">
      <SkeletonBar width={16} height={16} round />
      <SkeletonBar width={width} height={10} />
      <SkeletonBar width="12%" height={10} className="kp-sk-end" />
    </div>
  );
}

export interface SkeletonBarProps {
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
  /** Круг — для аватара. */
  round?: boolean;
  className?: string;
}

export function SkeletonBar({
  width = '100%',
  height = 12,
  round = false,
  className,
}: SkeletonBarProps) {
  return (
    <span
      aria-hidden="true"
      className={cx('t-sk', className)}
      style={{ width, height, borderRadius: round ? 'var(--radius-full)' : undefined }}
    />
  );
}
